import * as React from "react";

import { cn } from "../../lib/utils.js";

/**
 * Wrapper for an inline panel that opens and closes in place -- the Expenses
 * "Create Expense Report" and "Add Expense Item" cards, and anything like
 * them.
 *
 * Replaces `{open && <Panel />}`: that unmounts the moment the flag flips, so
 * the panel can only animate in, never out. This keeps the panel mounted until
 * its closing animation has finished, then drops it.
 *
 * How the content below moves -- the part that matters:
 *
 * Animating the wrapper's height (a `grid-template-rows` keyframe, a height
 * keyframe, or a per-frame height in JS) re-lays-out and repaints everything
 * below the panel on every frame. Thin borders -- the tab underline, every
 * table row rule -- land on different device pixels from frame to frame, so
 * they visibly stick, thicken and jitter, and a long table makes each of those
 * frames expensive.
 *
 * So the height changes exactly ONCE, and the content below is carried by a
 * `transform` instead: one reflow at the start, then the browser moves those
 * elements as composited layers. Nothing re-lays-out mid-flight, so nothing
 * can re-snap.
 */
export interface PanelRevealProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Open the panel; false plays the closing animation, then unmounts it. */
  open: boolean;
}

/** ms. Opening is the slower of the two: arriving is worth watching, leaving isn't. */
const OPEN_MS = 420;
/** ms. The panel fades out before the gap starts closing, never during. */
const FADE_OUT_MS = 160;
const CLOSE_MS = 280;
/** Expo-out: most of the distance early, then a long settle. */
const EASING = "cubic-bezier(0.16, 1, 0.3, 1)";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Content sliding under a stationary pointer lights the hover state of every
 * row it passes, one after another -- a flicker running down the table. The
 * flag lets CSS mute pointer events for the duration; the count keeps two
 * panels animating at once from clearing each other's flag. */
let animatingPanels = 0;
const markAnimating = (on: boolean) => {
  if (typeof document === "undefined") return;
  animatingPanels = Math.max(0, animatingPanels + (on ? 1 : -1));
  if (animatingPanels > 0) document.body.dataset["panelAnimating"] = "";
  else delete document.body.dataset["panelAnimating"];
};

/**
 * Slides everything after `el` from `distance` px back to its new resting
 * place. `fill: "none"` so each element is left exactly where layout already
 * put it -- the transform is only how it got there.
 */
function slideFollowing(el: HTMLElement, distance: number, duration: number): () => void {
  const running: Animation[] = [];
  for (let node = el.nextElementSibling; node; node = node.nextElementSibling) {
    if (!(node instanceof HTMLElement)) continue;
    const style = window.getComputedStyle(node);
    // A transform would make a fixed child position against this element
    // instead of the viewport -- modals and overlays must be left alone.
    if (style.position === "fixed" || style.display === "none") continue;
    const done = () => { node.style.willChange = ""; };
    node.style.willChange = "transform";
    const animation = node.animate(
      [{ transform: `translateY(${distance}px)` }, { transform: "translateY(0px)" }],
      { duration, easing: EASING, fill: "none" }
    );
    animation.onfinish = done;
    animation.oncancel = done;
    running.push(animation);
  }
  return () => running.forEach((a) => a.cancel());
}

const PanelReveal = React.forwardRef<HTMLDivElement, PanelRevealProps>(
  ({ open, className, children, ...props }, ref) => {
    const [mounted, setMounted] = React.useState(open);
    const nodeRef = React.useRef<HTMLDivElement | null>(null);
    const setRefs = React.useCallback((node: HTMLDivElement | null) => {
      nodeRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }, [ref]);

    // While closing, the panel keeps the content it had when it was open: the
    // caller usually clears the state behind it in the same click (an "Edit"
    // panel reverting to "Create"), and that would otherwise be visible
    // changing underneath the fade.
    const frozen = React.useRef(children);
    if (open) frozen.current = children;

    React.useEffect(() => {
      if (open) setMounted(true);
    }, [open]);

    // Layout effect: the panel is measured and the slide starts before the
    // browser paints, so nothing is ever seen in the wrong place.
    React.useLayoutEffect(() => {
      const el = nodeRef.current;
      if (!el) return;
      if (prefersReducedMotion()) {
        if (!open) setMounted(false);
        return;
      }

      let stopSlide: (() => void) | undefined;
      let timer = 0;
      // Idempotent: the timer and the cleanup both want to release the flag,
      // and a double release would clear it for a second panel still moving.
      let marked = true;
      markAnimating(true);
      const unmark = () => { if (marked) { marked = false; markAnimating(false); } };
      const cleanUp = () => { unmark(); window.clearTimeout(timer); stopSlide?.(); };

      if (open) {
        // The panel is already at full height, so everything below has been
        // pushed down by it: start them where they were and let them settle.
        stopSlide = slideFollowing(el, -el.offsetHeight, OPEN_MS);
        timer = window.setTimeout(unmark, OPEN_MS);
        return cleanUp;
      }

      // Closing: hold the gap open while the panel fades (CSS), then shut it
      // in one step and carry the content below up from where it was.
      timer = window.setTimeout(() => {
        const height = el.offsetHeight;
        el.style.height = "0px";
        stopSlide = slideFollowing(el, height, CLOSE_MS);
        timer = window.setTimeout(() => setMounted(false), CLOSE_MS);
      }, FADE_OUT_MS);
      return cleanUp;
    }, [open, mounted]);

    if (!mounted) return null;

    return (
      <div
        ref={setRefs}
        data-state={open ? "open" : "closed"}
        className={cn("panel-reveal", className)}
        {...props}
      >
        {open ? children : frozen.current}
      </div>
    );
  }
);
PanelReveal.displayName = "PanelReveal";

export { PanelReveal };
